/**
 * UpgradeModal - Reusable upgrade prompt modal
 * Shows real pricing when PRICING_PAGE feature is enabled,
 * otherwise falls back to Pac-Man game for free upgrade
 */

import { Logger } from '../utils/Logger';
import { updateUserTier, TIER_CONFIG } from '../api/UserProfileAPI';

const logger = Logger.getLogger('UpgradeModal');
import { showCustomModal, closeModal } from './ModalManager';
import { PacManGame, type GameResult } from './PacManGame';
import { isFeatureEnabled } from '../config/featureFlags';

/**
 * Show upgrade modal - either real pricing or Pac-Man game
 */
export async function showUpgradeModal(userId: string, _userEmail?: string): Promise<boolean> {
  if (isFeatureEnabled('PRICING_PAGE')) {
    // Show real pricing modal when pricing is enabled
    return showRealUpgradeModal();
  } else {
    // Fall back to Pac-Man game
    await showPacManGame(userId);
    return true;
  }
}

/**
 * Show real upgrade modal with pricing tiers
 */
function showRealUpgradeModal(): Promise<boolean> {
  return new Promise((resolve) => {
    const basicConfig = TIER_CONFIG.basic;
    const proConfig = TIER_CONFIG.pro;

    showCustomModal({
      title: 'Upgrade Your Plan',
      icon: '✨',
      theme: 'normal',
      customContent: `
        <div style="text-align: center; max-width: 520px; margin: 0 auto;">
          <p style="color: var(--ios-gray); margin-bottom: 24px; font-size: 15px; line-height: 1.5;">
            Unlock powerful features to create amazing VR experiences.
          </p>

          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 24px;">
            <!-- Basic Tier -->
            <div style="background: rgba(0,0,0,0.03); border: 1px solid rgba(0,0,0,0.08); border-radius: 16px; padding: 20px; text-align: center;">
              <div style="font-size: 13px; font-weight: 600; color: var(--ios-gray); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">Basic</div>
              <div style="font-size: 32px; font-weight: 700; color: var(--ios-text); margin-bottom: 4px;">
                $${typeof basicConfig.price === 'object' ? basicConfig.price.monthly : 0}
                <span style="font-size: 14px; font-weight: 400; color: var(--ios-gray);">/mo</span>
              </div>
              <div style="font-size: 12px; color: var(--ios-gray); margin-bottom: 16px;">${basicConfig.storageLimit >= 1024 * 1024 * 1024 ? Math.round(basicConfig.storageLimit / (1024 * 1024 * 1024)) + 'GB' : Math.round(basicConfig.storageLimit / (1024 * 1024)) + 'MB'} cloud storage</div>
              <ul style="text-align: left; font-size: 13px; color: var(--ios-text-secondary); padding-left: 0; margin: 0; list-style: none;">
                <li style="margin-bottom: 8px; padding-left: 20px; position: relative;"><span style="position: absolute; left: 0; color: var(--ios-green);">✓</span> Cloud hosting</li>
                <li style="margin-bottom: 8px; padding-left: 20px; position: relative;"><span style="position: absolute; left: 0; color: var(--ios-green);">✓</span> ${basicConfig.muxMinutesLimit >= 60 ? Math.round(basicConfig.muxMinutesLimit / 60) + ' hr' : basicConfig.muxMinutesLimit + ' min'} video uploads</li>
                <li style="margin-bottom: 0; padding-left: 20px; position: relative;"><span style="position: absolute; left: 0; color: var(--ios-green);">✓</span> Priority support</li>
              </ul>
            </div>

            <!-- Pro Tier (Highlighted) -->
            <div style="background: linear-gradient(135deg, rgba(255,107,107,0.08) 0%, rgba(78,205,196,0.06) 100%); border: 2px solid var(--primary); border-radius: 16px; padding: 20px; text-align: center; position: relative;">
              <div style="position: absolute; top: -12px; left: 50%; transform: translateX(-50%); background: linear-gradient(135deg, var(--ios-red) 0%, var(--ios-orange) 100%); color: white; font-size: 10px; font-weight: 700; padding: 5px 14px; border-radius: 12px; text-transform: uppercase; letter-spacing: 0.5px;">Popular</div>
              <div style="font-size: 13px; font-weight: 600; color: var(--ios-red); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px;">Professional</div>
              <div style="font-size: 32px; font-weight: 700; color: var(--ios-text); margin-bottom: 4px;">
                $${typeof proConfig.price === 'object' ? proConfig.price.monthly : 0}
                <span style="font-size: 14px; font-weight: 400; color: var(--ios-gray);">/mo</span>
              </div>
              <div style="font-size: 12px; color: var(--ios-gray); margin-bottom: 16px;">${proConfig.storageLimit >= 1024 * 1024 * 1024 ? Math.round(proConfig.storageLimit / (1024 * 1024 * 1024)) + 'GB' : Math.round(proConfig.storageLimit / (1024 * 1024)) + 'MB'} storage · ${proConfig.muxMinutesLimit >= 60 ? Math.round(proConfig.muxMinutesLimit / 60) + ' hr' : proConfig.muxMinutesLimit + ' min'} video</div>
              <ul style="text-align: left; font-size: 13px; color: var(--ios-text-secondary); padding-left: 0; margin: 0; list-style: none;">
                <li style="margin-bottom: 8px; padding-left: 20px; position: relative;"><span style="position: absolute; left: 0; color: var(--ios-red);">✓</span> AI 360° generation</li>
                <li style="margin-bottom: 8px; padding-left: 20px; position: relative;"><span style="position: absolute; left: 0; color: var(--ios-red);">✓</span> White-label branding</li>
                <li style="margin-bottom: 0; padding-left: 20px; position: relative;"><span style="position: absolute; left: 0; color: var(--ios-red);">✓</span> Advanced analytics</li>
              </ul>
            </div>
          </div>

          <p style="font-size: 12px; color: var(--ios-gray); margin-bottom: 0;">
            All plans include unlimited self-hosted experiences
          </p>
        </div>
      `,
      confirmText: 'View All Plans',
      cancelText: 'Maybe Later',
      showCancel: true,
      onConfirm: () => {
        window.location.href = '/dashboard/pricing.html';
        resolve(true);
      },
      onCancel: () => {
        resolve(false);
      }
    });
  });
}

/**
 * Show Pac-Man game
 */
async function showPacManGame(userId: string): Promise<void> {
  logger.debug('showPacManGame called for userId:', userId);

  return new Promise((resolve) => {
    // First show instructions
    showCustomModal({
      title: 'PAC-MAN',
      icon: '🟡',
      theme: 'pacman',
      customContent: `
        <div style="text-align: center; padding: 8px 12px;">
          <div style="font-size: 40px; margin-bottom: 20px; animation: pulse 1s infinite;">👻 🟡 👻</div>

          <p style="font-size: 11px; color: #ffcc00; margin-bottom: 24px; line-height: 1.8;">
            COLLECT ALL DOTS<br>TO UNLOCK PRO!
          </p>

          <div style="background: rgba(34, 68, 204, 0.3); border: 2px solid #2244cc; padding: 16px; border-radius: 8px; margin-bottom: 20px; text-align: left; font-size: 10px; line-height: 2.2;">
            <div style="color: #ffcc00;">🟡 YOU: PAC-MAN</div>
            <div style="color: #ff0000;">👻 BLINKY (RED)</div>
            <div style="color: #ffb8ff;">👻 PINKY</div>
            <div style="color: #00ffff;">👻 INKY</div>
            <div style="color: #ffb852;">👻 CLYDE</div>
          </div>

          <div style="background: rgba(255, 204, 0, 0.2); border: 2px solid #ffcc00; padding: 16px; border-radius: 8px; margin-bottom: 20px; text-align: left; font-size: 10px; line-height: 2.2;">
            <div>⚫ DOTS = 10 PTS</div>
            <div>🔵 POWER = 50 PTS</div>
            <div>👻 GHOST = 200 PTS</div>
            <div>❤️ LIVES = 3</div>
          </div>

          <div style="font-size: 9px; color: #2244cc; margin-bottom: 20px; line-height: 1.8;">
            ARROW KEYS<br>OR SWIPE
          </div>

          <div style="background: rgba(255, 0, 0, 0.2); border: 2px solid #ff0000; padding: 16px; border-radius: 8px; font-size: 10px; color: #ff6666; line-height: 1.8;">
            ⚠️ WIN = PRO ACCESS<br>
            LOSE = TRY AGAIN
          </div>
        </div>

        <style>
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
        </style>
      `,
      confirmText: 'PLAY',
      showCancel: true,
      cancelText: 'CANCEL',
      lockClose: false,
      preventAutoClose: true,
      onConfirm: async () => {
        // Start the actual game
        await startPacManGameplay(userId);
        resolve();
      },
      onCancel: () => {
        resolve();
      }
    });
  });
}

/**
 * Start Pac-Man gameplay (after instructions)
 */
async function startPacManGameplay(userId: string): Promise<void> {
  return new Promise((resolve) => {
    let game: PacManGame | null = null;

    logger.debug('Creating Pac-Man game modal...');

    showCustomModal({
      title: 'PAC-MAN',
      icon: '',
      theme: 'pacman',
      customContent: `
        <div style="text-align: center; padding: 0 8px;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 16px; font-size: 11px; padding: 0 4px;">
            <div style="color: #ffcc00;">SCORE: <span id="pacman-score">0</span></div>
            <div style="color: #ff6666;">LIVES: <span id="pacman-lives">❤️❤️❤️</span></div>
          </div>
          <canvas id="pacmanGameCanvas" style="border: 4px solid #2244cc; border-radius: 8px; display: block; margin: 0 auto; background: #000; max-width: 100%; box-shadow: 0 0 20px rgba(34, 68, 204, 0.4);"></canvas>
          <div style="font-size: 9px; color: #2244cc; margin-top: 16px; line-height: 1.6;">
            ARROWS OR SWIPE
          </div>
        </div>
      `,
      confirmText: undefined,
      showCancel: false,
      lockClose: true,
      preventAutoClose: true
    });

    // Start game after modal appears
    logger.debug('Waiting 500ms for modal to fully render...');

    setTimeout(() => {
      logger.debug('Looking for canvas element...');
      const canvas = document.getElementById('pacmanGameCanvas') as HTMLCanvasElement;

      if (!canvas) {
        logger.error('Pac-Man canvas not found!');
        closeModal();
        setTimeout(() => {
          showCustomModal({
            title: 'Error',
            message: 'Game failed to load. Please try again!',
            icon: '❌',
            theme: 'pacman',
            confirmText: 'Retry',
            showCancel: false,
            onConfirm: async () => {
              await showPacManGame(userId);
            }
          });
        }, 400);
        return;
      }

      logger.debug('Canvas found! Initializing Pac-Man...');

      try {
        game = new PacManGame(canvas);
        logger.debug('PacManGame instance created');

        // Get HTML elements for live updates
        const scoreEl = document.getElementById('pacman-score');
        const livesEl = document.getElementById('pacman-lives');

        game.start(
          // On Win
          async (result: GameResult) => {
            logger.info(`PAC-MAN COMPLETE! Score: ${result.score}`);
            if (game) game.dispose();
            await upgradeUserToPro(userId, result);
            resolve();
          },
          // On Lose
          async (result: GameResult) => {
            logger.info(`Game Over... Score: ${result.score}, Dots left: ${result.dotsRemaining}`);
            if (game) game.dispose();
            // No auto-win - must retry!
            await showGameOverRetry(userId, result);
            resolve();
          },
          // On Update - sync HTML display with game state
          (score: number, lives: number, _dotsRemaining: number) => {
            if (scoreEl) scoreEl.textContent = String(score);
            if (livesEl) livesEl.textContent = '❤️'.repeat(lives);
          }
        );

        logger.debug('Pac-Man game started!');
      } catch (error) {
        logger.error('Game initialization failed:', error);
        closeModal();
        setTimeout(() => {
          showCustomModal({
            title: 'Error',
            message: 'Game failed to start. Please try again!',
            icon: '❌',
            theme: 'pacman',
            confirmText: 'Retry',
            showCancel: false,
            onConfirm: async () => {
              await showPacManGame(userId);
            }
          });
        }, 400);
      }
    }, 500);
  });
}

/**
 * Upgrade user to Pro after winning Pac-Man
 */
async function upgradeUserToPro(userId: string, result: GameResult): Promise<void> {
  try {
    // Wait a bit to show the final score on the game screen
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Close game modal
    closeModal();

    // Small delay before showing victory modal
    await new Promise(resolve => setTimeout(resolve, 400));

    // Upgrade the user in the backend
    await updateUserTier(userId, 'pro');

    showCustomModal({
      title: 'YOU WIN',
      icon: '🏆',
      theme: 'pacman',
      customContent: `
        <div style="text-align: center;">
          <div style="font-size: 11px; color: #00ff00; margin-bottom: 20px; line-height: 2;">
            PERFECT!<br>
            ALL DOTS<br>
            COLLECTED!
          </div>

          <div style="font-size: 14px; color: #ffcc00; margin-bottom: 20px;">
            ${result.score}
          </div>

          <div style="background: rgba(0, 255, 0, 0.2); border: 2px solid #00ff00; padding: 16px; border-radius: 8px; font-size: 9px; color: #66ff66; line-height: 2;">
            🎉 PRO ACCESS<br>
            UNLOCKED!
          </div>
        </div>
      `,
      confirmText: 'AWESOME',
      showCancel: false,
      lockClose: false,
      onConfirm: () => {
        closeModal();
        // Update UI without reload by dispatching a custom event
        window.dispatchEvent(new CustomEvent('tier-updated', { detail: { tier: 'pro' } }));
      }
    });
  } catch (error) {
    logger.error('Upgrade failed:', error);
    closeModal();
    await new Promise(resolve => setTimeout(resolve, 400));
    const errorMessage = error instanceof Error ? error.message : 'Please try again.';
    showCustomModal({
      title: 'Upgrade Failed',
      message: `Failed to upgrade: ${errorMessage}`,
      icon: '❌',
      theme: 'pacman',
      confirmText: 'OK',
      showCancel: false
    });
  }
}

/**
 * Show game over screen with retry option (NO FREE PRO!)
 */
async function showGameOverRetry(userId: string, result: GameResult): Promise<void> {
  // Close game modal
  closeModal();

  // Small delay
  await new Promise(resolve => setTimeout(resolve, 400));

  showCustomModal({
    title: 'GAME OVER',
    icon: '💀',
    theme: 'pacman',
    customContent: `
      <div style="text-align: center;">
        <div style="font-size: 11px; color: #ff6666; margin-bottom: 20px; line-height: 2;">
          THE GHOSTS<br>GOT YOU!
        </div>

        <div style="font-size: 10px; color: #ffcc00; margin-bottom: 16px; line-height: 2;">
          SCORE: ${result.score}<br>
          DOTS LEFT: ${result.dotsRemaining}
        </div>

        <div style="background: rgba(255, 0, 0, 0.2); border: 2px solid #ff0000; padding: 12px; border-radius: 8px; font-size: 9px; color: #ff6666; line-height: 1.8; margin-bottom: 16px;">
          YOU MUST WIN<br>
          TO UNLOCK PRO!
        </div>
      </div>
    `,
    confirmText: 'RETRY',
    cancelText: 'QUIT',
    showCancel: true,
    onConfirm: async () => {
      // Restart the Pac-Man challenge
      await showPacManGame(userId);
    },
    onCancel: () => {
      // User gave up, just close
      logger.debug('User gave up on Pac-Man challenge');
    }
  });
}

/**
 * Show feature locked modal (quick prompt to upgrade)
 */
export async function showFeatureLockedModal(featureName: string): Promise<boolean> {
  return new Promise((resolve) => {
    showCustomModal({
      title: `🔒 ${featureName} is a Pro Feature`,
      message: `Upgrade to Pro to unlock ${featureName.toLowerCase()} and get full access to all premium features.`,
      icon: '⭐',
      confirmText: 'Upgrade to Pro',
      cancelText: 'Not Now',
      showCancel: true,
      onConfirm: () => resolve(true),
      onCancel: () => resolve(false)
    });
  });
}
